import React, { useState, useEffect, useRef, useCallback } from 'react';
import './WalletModal.css';
import { WalletConnectModal } from '@walletconnect/modal';
import { generateQrCodeFromUri } from '../utils/walletConnectConfig';

function WalletModal({ isOpen, onClose, onSelect }) {
  const [showAllWallets, setShowAllWallets] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyWalletConnect, setOnlyWalletConnect] = useState(false);
  const [visibleWallets, setVisibleWallets] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastIcon, setToastIcon] = useState(null);
  const [connectingState, setConnectingState] = useState(null); // null, 'connecting', 'error'
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [qrIsLoading, setQrIsLoading] = useState(true);
  const [wcUri, setWcUri] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [wcModal, setWcModal] = useState(null);
  const walletListRef = useRef(null);
  const ITEMS_PER_PAGE = 20;

  // Generate a larger list of wallets (about 40)
  const generateWalletList = () => {
    const baseWallets = [
      { name: "MetaMask", isWalletConnect: true, logo: "metamask", status: "INSTALLED" },
      { name: "Coinbase Wallet", isWalletConnect: true, logo: "coinbase", status: null },
      { name: "Trust Wallet", isWalletConnect: true, logo: "trust", status: null },
      { name: "Binance Wallet", isWalletConnect: false, logo: "binance", status: null },
      { name: "Phantom", isWalletConnect: false, logo: "phantom", status: null },
      { name: "Argent", isWalletConnect: true, logo: "argent", status: null },
      { name: "Rainbow", isWalletConnect: true, logo: "rainbow", status: null },
      { name: "Ledger Live", isWalletConnect: true, logo: "ledger", status: null },
      { name: "Exodus", isWalletConnect: true, logo: "exodus", status: null },
      { name: "Trezor", isWalletConnect: true, logo: "trezor", status: null }
    ];

    // Generate 40 wallets by repeating and modifying the base wallets
    const allWallets = [];
    for (let i = 0; i < 4; i++) {
      baseWallets.forEach((wallet, index) => {
        if (i === 0 && index === 0) {
          // Keep the first one as is (MetaMask with INSTALLED status)
          allWallets.push({ ...wallet });
        } else {
          const newWallet = { 
            ...wallet, 
            name: i > 0 ? `${wallet.name} ${i+1}` : wallet.name,
            isWalletConnect: Math.random() > 0.3 // 70% of wallets support WalletConnect
          };
          allWallets.push(newWallet);
        }
      });
    }
    
    return allWallets;
  };

  const walletList = generateWalletList();
  const totalWalletCount = walletList.length;

  // Show toast message
  const showToastMessage = useCallback((message, withIcon = null) => {
    setToastMessage(message);
    setToastIcon(withIcon);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  }, [setToastMessage, setToastIcon, setShowToast]);

  // Initialize WalletConnect modal
  useEffect(() => {
    if (!wcModal) {
      // Create WalletConnect Modal
      const modal = new WalletConnectModal({
        // Get the project ID from your environment variable or config
        projectId: process.env.REACT_APP_PROJECT_ID || 'YOUR_PROJECT_ID',
        chains: ['eip155:1'],
        explorerRecommendedWalletIds: [
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
          '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0'  // Trust Wallet
        ],
        enableExplorer: true,
        explorerExcludedWalletIds: [],
        mobileWallets: [],
        desktopWallets: []
      });
      
      setWcModal(modal);
    }
    
    return () => {
      // Cleanup
      if (wcModal) {
        wcModal.close();
      }
    };
  }, [wcModal]);

  // Handle WalletConnect connection
  const handleWalletConnectConnection = useCallback(async () => {
    if (wcModal) {
      try {
        setQrIsLoading(true);
        
        // Use the actual WalletConnect modal to connect
        try {
          // Set up event listeners for the modal
          wcModal.subscribeModal(({ open }) => {
            console.log('Modal state changed:', open);
            if (!open) {
              // Modal was closed
              setQrIsLoading(false);
            }
          });
          
          // Open the WalletConnect modal
          wcModal.openModal();
          
          // Generate a QR code for demonstration purposes
          // This would normally be handled by the WalletConnect modal itself
          setTimeout(async () => {
            // This URI would come from the WalletConnect client in a real implementation
            const uri = `wc:${Date.now()}?relay-protocol=irn&symKey=${Math.random().toString(36).substring(2)}`;
            setWcUri(uri);
            
            try {
              const qrCode = await generateQrCodeFromUri(uri);
              setQrCodeDataUrl(qrCode);
              setQrIsLoading(false);
            } catch (error) {
              console.error('Failed to generate QR code:', error);
              showToastMessage('Failed to generate QR code');
              setQrIsLoading(false);
            }
          }, 1000);
          
        } catch (error) {
          console.error('Modal opening error:', error);
          showToastMessage('Failed to open WalletConnect modal');
          setQrIsLoading(false);
        }
      } catch (error) {
        console.error('WalletConnect connection error:', error);
        showToastMessage('Connection failed');
        setQrIsLoading(false);
      }
    } else {
      showToastMessage('WalletConnect not initialized');
      setQrIsLoading(false);
    }
  }, [wcModal, showToastMessage]);

  // Helper function to get wallet image
  const getWalletImage = (logoName) => {
    return `/images/wallets/${logoName}.png`;
  };
  
  // WalletConnect icon for grid items
  const WalletConnectIcon = () => (
    <div className="wallet-connector-icon">
      <img src="/images/wallets/wc-icon-certified.svg" alt="WalletConnect Icon" width="12" height="12" />
    </div>
  );

  // Toast notification component
  const Toast = ({ message, icon }) => (
    <div className={`toast-notification ${showToast ? 'show' : ''}`}>
      {icon === 'success' && (
        <svg className="success-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      )}
      {message}
    </div>
  );

  // Render error state for MetaMask
  const renderErrorState = () => (
    <div className="connecting-error-container">
      <div className="error-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#FF4E4E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h3>Connection Error</h3>
      <p>Failed to connect to {selectedWallet?.name}. Please try again.</p>
      <button className="try-again-button" onClick={() => {
        setConnectingState(null);
        // Signal to parent that connection was canceled
        if (onSelect) {
          onSelect({...selectedWallet, connectionState: 'canceled'});
        }
      }}>
        Try Again
      </button>
    </div>
  );

  // Render connecting state for MetaMask
  const renderConnectingState = () => (
    <div className="connecting-container">
      <div className="connecting-loader">
        <div className="spinner"></div>
      </div>
      <h3>Connecting...</h3>
      <p>Connecting to {selectedWallet?.name}</p>
    </div>
  );

  const renderQrCodeView = () => (
    <>
      <div className="modal-header">
        <button className="back-button" onClick={handleBackClick}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2>WalletConnect</h2>
        <button className="close-button" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      <div className="qr-code-container">
        <div className="qr-code-wrapper">
          {qrIsLoading ? (
            <div className="qr-skeleton"></div>
          ) : (
            <div className="qr-code-image">
              {qrCodeDataUrl ? (
                <>
                  <img 
                    src={qrCodeDataUrl}
                    alt="WalletConnect QR Code" 
                  />
                  <div className="qr-overlay">
                    <img 
                      src="/images/wallets/wc-icon-certified.svg"
                      alt="WalletConnect Logo"
                    />
                  </div>
                </>
              ) : (
                <div className="qr-error">Failed to load QR code</div>
              )}
            </div>
          )}
        </div>
        
        <div className="qr-code-instructions">
          Scan this QR Code with your phone
        </div>
        
        <button 
          className="copy-link-button"
          onClick={handleCopyLink}
          disabled={qrIsLoading || !wcUri}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copy link
        </button>
        
        <div className="qr-attribution" style={{ marginTop: '15px', fontSize: '12px', opacity: 0.7 }}>
          UX by <span className="dot">·</span> <span className="slash">/</span> <span className="credit">rcown</span>
        </div>
      </div>
    </>
  );

  // Handle copy WalletConnect URI to clipboard
  const handleCopyLink = () => {
    if (!wcUri) return;
    
    navigator.clipboard.writeText(wcUri)
      .then(() => {
        showToastMessage('Link copied to clipboard', 'success');
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        showToastMessage('Failed to copy link');
      });
  };

  // Handle wallet selection
  const handleWalletSelect = async (wallet) => {
    setSelectedWallet(wallet);
    
    // Special handling for WalletConnect
    if (wallet.name === "WalletConnect") {
      setShowQrCode(true);
      setQrIsLoading(true);
      showToastMessage('Initializing WalletConnect...');
      
      try {
        await handleWalletConnectConnection();
      } catch (error) {
        console.error('WalletConnect initialization error:', error);
        showToastMessage('Failed to initialize WalletConnect');
      }
      return;
    }
    
    // Only perform MetaMask connection for MetaMask wallet
    if (wallet.name === "MetaMask" || wallet.name.includes("MetaMask")) {
      setConnectingState('connecting');
      showToastMessage('Connecting to MetaMask...');
      
      try {
        // Check if MetaMask is installed
        if (window.ethereum) {
          try {
            // Request account access
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            
            // Connection successful
            if (accounts && accounts.length > 0) {
              // Call onSelect to pass the connected wallet back to the parent component
              if (onSelect) {
                // Pass the connection state as 'connected' and then 'error' after timeout
                onSelect({...wallet, address: accounts[0], connectionState: 'connected'});
                
                // Show toast for successful connection
                showToastMessage(`Connected to ${wallet.name}`);
                
                // Even though connection is successful, show error after 3 seconds
                setTimeout(() => {
                  setConnectingState('error');
                  showToastMessage('Connection error occurred');
                  
                  // Notify parent with error state
                  if (onSelect) {
                    onSelect({...wallet, address: accounts[0], connectionState: 'error'});
                  }
                }, 3000);
              }
            } else {
              setConnectingState('error');
              showToastMessage('No accounts found');
              if (onSelect) {
                onSelect({...wallet, connectionState: 'error'});
              }
            }
          } catch (requestError) {
            // User rejected or another error
            console.error('Error requesting accounts:', requestError);
            setConnectingState('error');
            showToastMessage(`Error: ${requestError.message || 'User rejected the request'}`);
            if (onSelect) {
              onSelect({...wallet, connectionState: 'error'});
            }
          }
        } else if (window.web3) {
          // Legacy web3 detection
          showToastMessage('Using legacy web3 provider');
          try {
            const accounts = await window.web3.eth.getAccounts();
            if (accounts && accounts.length > 0) {
              if (onSelect) {
                onSelect({...wallet, address: accounts[0], connectionState: 'connected'});
              }
              showToastMessage(`Connected to ${wallet.name}`);
              
              // Show error after 3 seconds even for successful connection
              setTimeout(() => {
                setConnectingState('error');
                showToastMessage('Connection error occurred');
                if (onSelect) {
                  onSelect({...wallet, address: accounts[0], connectionState: 'error'});
                }
              }, 3000);
            } else {
              setConnectingState('error');
              showToastMessage('No accounts found');
              if (onSelect) {
                onSelect({...wallet, connectionState: 'error'});
              }
            }
          } catch (web3Error) {
            console.error('Error with legacy web3:', web3Error);
            setConnectingState('error');
            showToastMessage(`Error: ${web3Error.message || 'Failed to connect'}`);
            if (onSelect) {
              onSelect({...wallet, connectionState: 'error'});
            }
          }
        } else {
          // MetaMask not installed
          console.log('MetaMask is not installed');
          setConnectingState('error');
          showToastMessage('MetaMask is not installed. Please install MetaMask and try again.');
          if (onSelect) {
            onSelect({...wallet, connectionState: 'error'});
          }
        }
      } catch (error) {
        // Error connecting to MetaMask
        console.error('Error connecting to MetaMask:', error);
        setConnectingState('error');
        showToastMessage(`Error: ${error.message || 'Failed to connect'}`);
        if (onSelect) {
          onSelect({...wallet, connectionState: 'error'});
        }
      }
    } else {
      // For all other wallets, just call onSelect without connection
      if (onSelect) {
        onSelect(wallet);
      }
      showToastMessage(`Connected to ${wallet.name}`);
    }
  };
  
  // Cleanup WalletConnect on unmount or modal close
  useEffect(() => {
    return () => {
      if (wcModal) {
        wcModal.close();
      }
    };
  }, [wcModal]);

  // Memoize loadMoreWallets to prevent unnecessary recreations
  const loadMoreWallets = useCallback((nextPage) => {
    const filteredWallets = walletList.filter(wallet => {
      const matchesSearch = wallet.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = onlyWalletConnect ? wallet.isWalletConnect : true;
      return matchesSearch && matchesFilter;
    });

    const start = (nextPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const newItems = filteredWallets.slice(start, end);

    if (newItems.length === 0) {
      setHasMore(false);
      return;
    }

    setVisibleWallets(prev => nextPage === 1 ? newItems : [...prev, ...newItems]);
    setPage(nextPage);
    setHasMore(end < filteredWallets.length);
  }, [searchQuery, onlyWalletConnect, walletList, ITEMS_PER_PAGE]);

  const handleAllWalletsClick = () => {
    setIsLoading(true);
    setShowAllWallets(true);
    setPage(1);
    setVisibleWallets([]);
    
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };

  const handleBackClick = () => {
    if (showQrCode) {
      setShowQrCode(false);
    } else if (showAllWallets) {
      setShowAllWallets(false);
      setVisibleWallets([]);
      setPage(1);
      setHasMore(true);
    }
  };

  const handleQrCodeClick = () => {
    setShowQrCode(true);
    setQrIsLoading(true);
    handleWalletConnectConnection();
  };

  const toggleWalletConnectFilter = () => {
    const newState = !onlyWalletConnect;
    setOnlyWalletConnect(newState);
    setPage(1);
    setVisibleWallets([]);
    
    // Show toast message
    if (newState) {
      showToastMessage('Showing WalletConnect compatible wallets');
    } else {
      showToastMessage('Showing all wallets');
    }
    
    setTimeout(() => {
      loadMoreWallets(1);
    }, 100);
  };

  const handleScroll = (e) => {
    if (!walletListRef.current || isLoading) return;
    
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    
    // Load more when user scrolls to bottom (with a 100px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore) {
      loadMoreWallets(page + 1);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
    setVisibleWallets([]);
    setTimeout(() => {
      loadMoreWallets(1);
    }, 300);
  };

  // Show skeleton loaders when loading
  const skeletonLoaders = Array(9).fill(0).map((_, index) => (
    <div key={`skeleton-${index}`} className="wallet-grid-item skeleton">
      <div className="wallet-grid-logo skeleton-circle"></div>
      <div className="skeleton-text skeleton-short"></div>
    </div>
  ));

  // Load initial wallets when entering the All Wallets view
  useEffect(() => {
    if (showAllWallets && !isLoading && visibleWallets.length === 0) {
      loadMoreWallets(1);
    }
  }, [showAllWallets, isLoading, loadMoreWallets, visibleWallets.length]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {connectingState === 'connecting' && renderConnectingState()}
        {connectingState === 'error' && renderErrorState()}
        {!connectingState && (
          showQrCode ? (
            renderQrCodeView()
          ) : !showAllWallets ? (
            <>
              <div className="modal-header">
                <button className="help-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </button>
                <h2>Connect Wallet</h2>
                <button className="close-button" onClick={onClose}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              
              <div className="wallet-options">
                <div className="wallet-option" onClick={handleQrCodeClick}>
                  <div className="wallet-icon">
                    <img src="/images/wallets/wc-icon-certified.svg" alt="WalletConnect Logo" style={{width: '36px', height: '36px'}} />
                  </div>
                  <div className="wallet-name">WalletConnect</div>
                  <button className="wallet-status qr-code" onClick={handleQrCodeClick}>QR CODE</button>
                </div>
                
                <div className="wallet-option" onClick={() => handleWalletSelect({ name: "MetaMask", isWalletConnect: true, logo: "metamask", status: "INSTALLED" })}>
                  <div className="wallet-icon">
                    <img src={getWalletImage('metamask')} alt="MetaMask Logo" />
                  </div>
                  <div className="wallet-name">MetaMask</div>
                  <div className="wallet-status installed">INSTALLED</div>
                </div>
                
                <div className="wallet-option" onClick={handleAllWalletsClick}>
                  <div className="wallet-icon all-wallets">
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="36" height="36" rx="8" fill="#141526"/>
                      <circle cx="12" cy="12" r="3" fill="#4256B8"/>
                      <circle cx="12" cy="24" r="3" fill="#4256B8"/>
                      <circle cx="24" cy="12" r="3" fill="#4256B8"/>
                      <circle cx="24" cy="24" r="3" fill="#4256B8"/>
                    </svg>
                  </div>
                  <div className="wallet-name">All Wallets</div>
                  <div className="wallet-status count">{totalWalletCount}</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="modal-header">
                <button className="back-button" onClick={handleBackClick}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
                <h2>All Wallets</h2>
                <button className="close-button" onClick={onClose}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              
              <div className="wallet-list-container">
                <div className="search-and-filter-container">
                  <div className="search-input-wrapper">
                    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input 
                      type="text" 
                      className="search-input" 
                      placeholder="Search wallet" 
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </div>
                  
                  <div className="toggle-switch-container">
                    <div className="wallet-connect-icon">
                      <img src="/images/wallets/wc-icon-certified.svg" alt="WalletConnect Logo" width="22" height="22" />
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={onlyWalletConnect}
                        onChange={toggleWalletConnectFilter}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
                
                <div 
                  className="wallet-grid" 
                  ref={walletListRef}
                  onScroll={handleScroll}
                >
                  {isLoading ? (
                    skeletonLoaders
                  ) : (
                    <>
                      {visibleWallets.map((wallet, index) => (
                        <div key={index} className="wallet-grid-item" onClick={() => handleWalletSelect(wallet)}>
                          <div className="wallet-grid-logo">
                            <img src={getWalletImage(wallet.logo)} alt={`${wallet.name} Logo`} />
                          </div>
                          <div className="wallet-grid-name">{wallet.name}</div>
                          {wallet.isWalletConnect && <WalletConnectIcon />}
                        </div>
                      ))}
                      {!isLoading && hasMore && (
                        <div className="loading-more">
                          <div className="loading-dot"></div>
                          <div className="loading-dot"></div>
                          <div className="loading-dot"></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )
        )}
        
        {/* Toast notification */}
        <Toast message={toastMessage} icon={toastIcon} />
      </div>
    </div>
  );
}

export default WalletModal; 